import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useClients } from "@/components/ClientSelect";
import { useStaff } from "@/components/StudyMeta";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Search, Trash2, Loader2, Plus, FileText, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, money } from "@/lib/api";
import { ScoreRing, VerdictBadge } from "@/components/ScoreRing";
import { LowRatingAlerts } from "@/components/LowRatingAlerts";
import { StudyRequests } from "@/components/StudyRequests";
import { ImproveRequests } from "@/components/ImproveRequests";
import { PricingRequests } from "@/components/PricingRequests";
import { DueSoon } from "@/components/DueSoon";
import { InviteFormButton } from "@/components/InviteFormDialog";
import { DeadlineBadge } from "@/components/DeadlineBadge";
import { SwipeActions, AssignDialog } from "@/components/SwipeActions";

const Kpi = ({ label, value, testId }) => (
  <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-6">
    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{label}</p>
    <p data-testid={testId} className="num text-3xl font-bold">{value}</p>
  </div>
);

const StudyCard = ({ s, onDelete, clientName, revs, assignee, due }) => (
  <div className="group relative rounded-xl border border-slate-800 bg-slate-900/70 p-6 hover:border-emerald-500/40 hover:bg-slate-900 transition-colors" data-testid={`study-card-${s.id}`}>
    <Link to={`/studies/${s.id}`} className="absolute inset-0" data-testid={`open-study-${s.id}`} aria-label="Open study" />
    <div className="flex items-start justify-between gap-4 mb-4">
      {s.status === "ready" ? <ScoreRing score={s.score} size={56} /> : <span className="h-14 w-14 grid place-items-center rounded-full border border-slate-700">{s.status === "generating" ? <Loader2 className="h-5 w-5 animate-spin text-emerald-400" /> : <FileText className="h-5 w-5 text-rose-400" />}</span>}
      <Button size="icon" variant="ghost" data-testid={`delete-study-${s.id}`} className="relative z-10 opacity-60 hover:opacity-100 hover:text-rose-400" onClick={() => onDelete(s.id)}><Trash2 className="h-4 w-4" /></Button>
    </div>
    <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">{s.input.industry || "General"}{revs > 0 && <span data-testid={`study-revs-${s.id}`} className="ml-2 normal-case tracking-normal px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300">{revs} change request{revs > 1 ? "s" : ""}</span>}</p>
    <h3 className="font-display text-lg font-semibold tracking-tight mb-1">{s.input.project_name || "Naming your study…"}</h3>
    {due && <div className="mb-2"><DeadlineBadge date={s.loan_deadline} testId={`study-deadline-${s.id}`} /></div>}
    {assignee && <p className="text-[11px] text-slate-400 mb-1" data-testid={`study-assignee-${s.id}`}>Assigned: <span className="text-slate-200">{assignee}</span></p>}
    {s.input.address ? <p className="flex items-center gap-1 text-xs text-cyan-300 mb-3 truncate"><MapPin className="h-3 w-3 shrink-0" />{s.input.address}</p> : <div className="mb-3" />}
    <div className="flex items-center justify-between text-sm">
      {s.status === "ready" ? <VerdictBadge verdict={s.verdict} /> : <span className="text-xs text-slate-400 capitalize">{s.status}…</span>}
      <span className="num text-slate-400">{clientName ? <span className="font-sans text-xs text-emerald-300" data-testid={`study-client-${s.id}`}>{clientName}</span> : money(s.input.budget, s.input.currency)}</span>
    </div>
  </div>
);

export default function Dashboard() {
  const [studies, setStudies] = useState(null);
  const [dueIds, setDueIds] = useState(new Set());
  const [q, setQ] = useState("");
  const [params] = useSearchParams();
  const clientId = params.get("client");
  const clients = useClients();
  const clientName = (cid) => clients.find((c) => c.id === cid)?.name;
  const [openRevs, setOpenRevs] = useState({});
  const staff = useStaff();
  const { user } = useAuth();
  const [mine, setMine] = useState(false);
  const [assigning, setAssigning] = useState(null);
  const assigned = (s) => setStudies((l) => l.map((x) => (x.id === s.id ? s : x)));
  const staffName = (id) => staff.find((s) => s.id === id)?.name;
  useEffect(() => { api.get("/revisions/open-counts").then((r) => setOpenRevs(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/studies", { params: clientId ? { client_id: clientId } : {} }).then((r) => setStudies(r.data)); }, [clientId]);

  const remove = async (id) => {
    if (!window.confirm("Delete this study?")) return;
    try {
      await api.delete(`/studies/${id}`);
      setStudies((s) => s.filter((x) => x.id !== id));
      toast.success("Study deleted");
    } catch {
      toast.error("Could not delete study");
    }
  };

  const list = (studies || []).filter((s) => !mine || s.assigned_to === user?.id).filter((s) => `${s.input.project_name} ${s.input.industry} ${s.input.address || ""}`.toLowerCase().includes(q.toLowerCase()));
  const ready = (studies || []).filter((s) => s.status === "ready");
  const avg = ready.length ? Math.round(ready.reduce((a, s) => a + (s.score || 0), 0) / ready.length) : "–";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-2">Workspace</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">{clientId ? `Studies — ${clientName(clientId) || "client"}` : "Studies"}</h1>
        </div>
        <div className="flex flex-wrap sm:flex-nowrap gap-3 w-full sm:w-auto items-center">
        <InviteFormButton />
        <button data-testid="filter-mine" onClick={() => setMine((m) => !m)} className={`shrink-0 px-4 py-2 rounded-full text-sm border transition-colors ${mine ? "bg-emerald-500 text-emerald-950 border-emerald-500" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}>Assigned to me</button>
        <div className="relative w-full sm:w-72">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input data-testid="studies-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search studies" className="pl-9 bg-slate-900 border-slate-700" />
        </div>
        </div>
      </div>
      <LowRatingAlerts />
      <DueSoon onLoad={setDueIds} />
      <PricingRequests />
      <StudyRequests />
      <ImproveRequests />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
        <Kpi label="Total studies" value={studies?.length ?? "–"} testId="kpi-total" />
        <Kpi label="Avg feasibility" value={avg} testId="kpi-avg-score" />
        <Kpi label="Go verdicts" value={ready.filter((s) => s.verdict === "Go").length} testId="kpi-go" />
      </div>
      {studies === null ? <Loader2 className="h-6 w-6 animate-spin text-emerald-400" /> : list.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-700 p-16 text-center" data-testid="studies-empty">
          <p className="text-slate-400 mb-4">{mine ? "No studies assigned to you." : q && studies.length ? "No matching studies." : "No studies yet."}</p>
          {!mine && <Button asChild data-testid="empty-new-study" className="rounded-full bg-emerald-500 text-emerald-950"><Link to="/new"><Plus className="h-4 w-4 mr-1" />Create your first study</Link></Button>}
        </div>
      ) : (
        <>
          <p className="md:hidden text-xs text-slate-500 mb-3" data-testid="swipe-hint">Swipe a card left for quick actions.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="studies-grid">{list.map((s) => (
            <SwipeActions key={s.id} study={s} canAssign={user?.role === "admin"} onAssign={setAssigning} onDelete={remove}>
              <StudyCard s={s} onDelete={remove} clientName={clientName(s.client_id)} revs={openRevs[s.id]} assignee={staffName(s.assigned_to)} due={dueIds.has(s.id)} />
            </SwipeActions>
          ))}</div>
          <AssignDialog study={assigning} staff={staff} onClose={() => setAssigning(null)} onAssigned={assigned} />
        </>
      )}
    </div>
  );
}
